/**
 * EmailInput.tsx - 統一メールアドレス入力コンポーネント
 * FL-148: 全てのメールアドレス入力フォームに有効性バリデーションチェックを追加
 *
 * リアルタイムバリデーション、統一エラーメッセージ、アクセシビリティ対応を含む
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { FormInput } from './common/FormInput';
import { validateEmail, EmailValidationOptions } from '../../utils/emailValidation';
import { isTestEnvironment } from '../../utils/environment';
import logger from '../../utils/logger';

interface EmailInputProps {
  /** 現在の値 */
  value: string;
  /** 値変更時のコールバック */
  onChange: (name: string, value: string) => void;
  /** フィールド名 */
  name?: string;
  /** ラベル */
  label?: string;
  /** プレースホルダー */
  placeholder?: string;
  /** 必須フィールドかどうか */
  required?: boolean;
  /** 無効化フラグ */
  disabled?: boolean;
  /** 初期エラーメッセージ */
  error?: string;
  /** バリデーションオプション */
  validationOptions?: EmailValidationOptions;
  /** リアルタイムバリデーションを有効にするかどうか */
  enableRealtimeValidation?: boolean;
  /** バリデーション結果変更時のコールバック */
  onValidationChange?: (isValid: boolean, error?: string) => void;
  /** フォーカス時のコールバック */
  onFocus?: () => void;
  /** ブラー時のコールバック */
  onBlur?: () => void;
  /** CSS クラス */
  className?: string;
  /** テスト用ID */
  'data-testid'?: string;
}

export const EmailInput: React.FC<EmailInputProps> = ({
  value,
  onChange,
  name = 'email',
  label = 'メールアドレス',
  placeholder = 'example@company.com',
  required = false,
  disabled = false,
  error: externalError,
  validationOptions,
  enableRealtimeValidation = true,
  onValidationChange,
  onFocus,
  onBlur,
  className,
  'data-testid': testId,
}) => {
  const [internalError, setInternalError] = useState<string>('');
  const [warning, setWarning] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(false);
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [isTyping, setIsTyping] = useState<boolean>(false);

  // デバウンス用のタイマーRef
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // バリデーション実行（安定化版）
  const performValidation = useCallback(
    (emailValue: string) => {
      try {
        // プロパティを直接使用してクロージャ問題を回避
        const currentValidationOpts = {
          required: required,
          strict: true,
          allowInternational: true,
          ...(validationOptions || {}),
        };

        const result = validateEmail(emailValue, currentValidationOpts);

        // 状態更新をバッチング
        setIsValid(result.isValid);
        setInternalError(result.isValid ? '' : result.error || '');
        setWarning(result.warning || '');
        setIsTyping(false); // 成功時は入力完了

        // コールバックは最後に呼び出す
        try {
          if (onValidationChange && typeof onValidationChange === 'function') {
            onValidationChange(result.isValid, result.error);
          }
        } catch (callbackError) {
          logger.error('Validation callback error:', callbackError);
        }

        logger.debug('Email validation result:', {
          email: emailValue,
          isValid: result.isValid,
          error: result.error,
          warning: result.warning,
        });
      } catch (error) {
        logger.error('Email validation error:', error);
        setIsValid(false);
        setInternalError('バリデーション処理でエラーが発生しました');
        setWarning('');
        setIsTyping(false); // エラー時も入力完了

        // エラー時もコールバックを呼び出す
        try {
          if (onValidationChange && typeof onValidationChange === 'function') {
            onValidationChange(false, 'バリデーション処理でエラーが発生しました');
          }
        } catch (callbackError) {
          logger.error('Validation callback error in catch:', callbackError);
        }
      }
    },
    [required, validationOptions, onValidationChange]
  );

  // 値の変更時にバリデーションを実行（本番：デバウンス有効、テスト：デバウンス無効）
  useEffect(() => {
    if (enableRealtimeValidation && (isTouched || value)) {
      if (isTestEnvironment()) {
        // テスト環境では即座にバリデーション実行
        performValidation(value);
      } else {
        // 本番環境では300msデバウンス
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
          performValidation(value);
        }, 300);
      }
    }

    // クリーンアップ関数
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [value, enableRealtimeValidation, isTouched, performValidation]);

  // 入力変更ハンドラー
  const handleChange = (fieldName: string, newValue: string) => {
    setIsTyping(true);
    setInternalError(''); // 入力中はエラーを一時的にクリア
    onChange(fieldName, newValue);
  };

  // フォーカスハンドラー
  const handleFocus = () => {
    setIsTouched(true);
    if (onFocus) {
      onFocus();
    }
  };

  // ブラーハンドラー
  const handleBlur = () => {
    setIsTyping(false);

    // デバウンスタイマーをクリア
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // ブラー時は即座にバリデーション実行
    if (enableRealtimeValidation) {
      performValidation(value);
    }
    if (onBlur) {
      onBlur();
    }
  };

  // 表示するエラーメッセージを決定
  const displayError = externalError || internalError;

  // バリデーション状態の表示内容を決定
  const getValidationDisplay = () => {
    // 入力中は表示を控えめに（ただし、テスト環境では表示しない）
    if (isTyping && enableRealtimeValidation && !isTestEnvironment()) {
      return (
        <div className='flex items-center mt-1 text-sm text-blue-600 dark:text-blue-400'>
          <div className='animate-pulse w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full mr-2'></div>
          入力中...
        </div>
      );
    }

    // エラーがある場合
    if (displayError && isTouched) {
      return (
        <div className='flex items-center mt-1 text-sm text-red-600 dark:text-red-400'>
          <ExclamationTriangleIcon className='h-4 w-4 mr-1 flex-shrink-0' />
          {displayError}
        </div>
      );
    }

    // 警告がある場合
    if (warning && value && isTouched && !displayError) {
      return (
        <div className='flex items-center mt-1 text-sm text-yellow-600 dark:text-yellow-400'>
          <ExclamationCircleIcon className='h-4 w-4 mr-1 flex-shrink-0' />
          {warning}
        </div>
      );
    }

    // 有効な場合の成功表示
    if (isValid && value && isTouched && !displayError) {
      return (
        <div className='flex items-center mt-1 text-sm text-green-600 dark:text-green-400'>
          <CheckCircleIcon className='h-4 w-4 mr-1 flex-shrink-0' />
          有効なメールアドレスです
        </div>
      );
    }

    return null;
  };

  return (
    <div className={`space-y-1 ${className || ''}`}>
      <FormInput
        name={name}
        label={label}
        type='email'
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        debounceMs={0} // デバウンスは内部で管理
        autoComplete='email'
        error={displayError}
        data-testid={testId}
        aria-invalid={!!displayError}
        aria-describedby={displayError ? `${name}-error` : undefined}
      />

      {/* バリデーション状態の表示 */}
      <div id={`${name}-validation`} role='status' aria-live='polite'>
        {getValidationDisplay()}
      </div>
    </div>
  );
};

export default EmailInput;
